/**
 * Markdown for ogreclan.com's posts and pages, read into blocks and inline pieces that markdown.tsx draws: never HTML,
 * so nothing written can run a script. It's the usual Markdown (headings, **bold**, *italic*, ~~struck~~, `code`,
 * links, images, lists, quotes, tables, code blocks, lines), where a line break is a line break, as in Discord, plus
 * two things of OgreClan's own: a Pokepaste link shows its team, and Pokemon names get their sprites (pokemonMatcher).
 */

export type Inline =
	{ type: 'text', text: string } | { type: 'code', text: string } | { type: 'break' } |
	{ type: 'strong' | 'em' | 'del', children: Inline[] } |
	{ type: 'link', href: string, children: Inline[] } | { type: 'image', src: string, alt: string };

export type Align = '' | 'left' | 'center' | 'right';

export type Block =
	{ type: 'heading', level: number, inline: Inline[] } | { type: 'paragraph', inline: Inline[] } |
	{ type: 'code', text: string } | { type: 'quote', blocks: Block[] } | { type: 'rule' } |
	{ type: 'list', ordered: boolean, start: number, items: Block[][] } |
	{ type: 'table', align: Align[], head: Inline[][], rows: Inline[][][] } |
	/** A Pokepaste: its team's sprites and title, linking to it */
	{ type: 'paste', id: string };

const PASTE = /^https?:\/\/(?:www\.)?pokepast\.es\/([0-9a-f]{8,32})\/?(?:[?#].*)?$/i;

/** A Pokepaste link's paste ID, or null */
export function pokepasteId(url: string) {
	return PASTE.exec(url.trim())?.[1]?.toLowerCase() ?? null;
}

/** Links go to web pages, email, or the site itself; images come from https only */
export function safeHref(url: string) {
	const href = url.trim();
	if (/^(https?:|mailto:)/i.test(href) || /^\/(?!\/)/.test(href) || href.startsWith('#')) return href;
	return null;
}

/* Inline */

const LINK = /^\[((?:[^[\]\\]|\\.|\[[^\]]*\])*)\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"]*")?\s*\)/;
const IMAGE = /^!\[([^\]]*)\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"]*")?\s*\)/;
const AUTOLINK = /^<(https?:\/\/[^>\s]+)>/;
const BARE_URL = /^https?:\/\/[^\s<>]+/;

/** Where a ** or __ (or *, _, ~~) that opens at `start` closes, or -1 */
function closing(text: string, marker: string, start: number) {
	for (let i = start; i < text.length; i++) {
		if (text[i] === '\\') {
			i++;
			continue;
		}
		if (text[i] === '`') {
			const end = text.indexOf('`', i + 1);
			if (end > 0) i = end;
			continue;
		}
		if (text.startsWith(marker, i) && i > start && !/\s/.test(text[i - 1]!)) {
			// _ only closes at a word's end (snake_case isn't emphasis)
			if (marker[0] === '_' && /\w/.test(text[i + marker.length] ?? '')) continue;
			// a single * isn't the start of a closing **
			if (marker === '*' && text[i + 1] === '*') {
				i++;
				continue;
			}
			return i;
		}
	}
	return -1;
}

export function parseInline(text: string): Inline[] {
	const out: Inline[] = [];
	let plain = '';
	const flush = () => {
		if (plain) out.push({ type: 'text', text: plain });
		plain = '';
	};
	const push = (node: Inline) => {
		flush();
		out.push(node);
	};
	for (let i = 0; i < text.length;) {
		const rest = text.slice(i);
		const ch = text[i]!;
		let match;
		if (ch === '\\' && i + 1 < text.length && /[\\`*_{}[\]()#+\-.!~|>]/.test(text[i + 1]!)) {
			plain += text[i + 1];
			i += 2;
		} else if (ch === '\n') {
			push({ type: 'break' });
			i++;
		} else if (ch === '`' && text.indexOf('`', i + 1) > i) {
			const end = text.indexOf('`', i + 1);
			push({ type: 'code', text: text.slice(i + 1, end) });
			i = end + 1;
		} else if (ch === '!' && (match = IMAGE.exec(rest))) {
			const src = match[2]!;
			if (/^https:\/\//i.test(src)) push({ type: 'image', src, alt: match[1]! });
			else plain += match[0];
			i += match[0].length;
		} else if (ch === '[' && (match = LINK.exec(rest))) {
			const href = safeHref(match[2]!);
			if (href) push({ type: 'link', href, children: parseInline(match[1]!) });
			else plain += match[1];
			i += match[0].length;
		} else if (ch === '<' && (match = AUTOLINK.exec(rest))) {
			push({ type: 'link', href: match[1]!, children: [{ type: 'text', text: match[1]! }] });
			i += match[0].length;
		} else if ((ch === 'h' || ch === 'H') && !/[\w/]/.test(text[i - 1] ?? '') && (match = BARE_URL.exec(rest))) {
			// a full stop or bracket after a link isn't part of it
			let url = match[0];
			while (/[.,;:!?'")\]]$/.test(url) && !(url.endsWith(')') && url.includes('('))) url = url.slice(0, -1);
			push({ type: 'link', href: url, children: [{ type: 'text', text: url }] });
			i += url.length;
		} else if ((ch === '*' || ch === '_' || ch === '~') && !(ch === '_' && /\w/.test(text[i - 1] ?? ''))) {
			const double = text[i + 1] === ch;
			const marker = ch === '~' ? (double ? '~~' : '') : double ? ch + ch : ch;
			const end = marker && !/\s/.test(text[i + marker.length] ?? ' ') ? closing(text, marker, i + marker.length) : -1;
			if (end > 0) {
				const children = parseInline(text.slice(i + marker.length, end));
				push({ type: marker === '~~' ? 'del' : marker.length === 2 ? 'strong' : 'em', children });
				i = end + marker.length;
			} else {
				plain += ch;
				i++;
			}
		} else {
			plain += ch;
			i++;
		}
	}
	flush();
	return out;
}

/** The text of some inline pieces, without their formatting */
export function inlineText(nodes: Inline[]): string {
	return nodes.map(node => 'children' in node ? inlineText(node.children) : 'text' in node ? node.text : node.type === 'image' ? node.alt : '\n').join('');
}

/* Blocks */

const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const RULE = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
const FENCE = /^\s{0,3}(```|~~~)/;
const BULLET = /^(\s{0,3})([-*+])\s+(.*)$/;
const NUMBERED = /^(\s{0,3})(\d{1,9})[.)]\s+(.*)$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const TABLE_RULE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

function tableCells(line: string) {
	let row = line.trim();
	if (row.startsWith('|')) row = row.slice(1);
	if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
	return row.split(/(?<!\\)\|/).map(cell => cell.trim().replace(/\\\|/g, '|'));
}

/** A paragraph that is a Pokepaste link and nothing else is that paste's team; one with Pokepaste links in it shows
 * their teams after it */
function paragraph(text: string): Block[] {
	const inline = parseInline(text);
	const meaningful = inline.filter(node => !(node.type === 'text' && !node.text.trim()) && node.type !== 'break');
	if (meaningful.length === 1 && meaningful[0]!.type === 'link') {
		const id = pokepasteId(meaningful[0]!.href);
		if (id) return [{ type: 'paste', id }];
	}
	const pastes: string[] = [];
	const find = (nodes: Inline[]) => {
		for (const node of nodes) {
			if (node.type === 'link') {
				const id = pokepasteId(node.href);
				if (id && !pastes.includes(id)) pastes.push(id);
			} else if ('children' in node) {
				find(node.children);
			}
		}
	};
	find(inline);
	return [{ type: 'paragraph', inline }, ...pastes.map(id => ({ type: 'paste', id }) as const)];
}

export function parseBlocks(markdown: string): Block[] {
	const lines = `${markdown ?? ''}`.replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n');
	const blocks: Block[] = [];
	let i = 0;
	const blank = (line: string | undefined) => line === undefined || !line.trim();
	/** A line that starts a block of its own, so it ends a paragraph */
	const starts = (line: string, next: string | undefined) => HEADING.test(line) || RULE.test(line) || FENCE.test(line) ||
		BULLET.test(line) || NUMBERED.test(line) || QUOTE.test(line) || (line.includes('|') && !!next && TABLE_RULE.test(next) && next.includes('-'));

	while (i < lines.length) {
		const line = lines[i]!;
		if (blank(line)) {
			i++;
			continue;
		}
		let match;
		if ((match = FENCE.exec(line))) {
			const fence = match[1]!;
			const code: string[] = [];
			i++;
			while (i < lines.length && !lines[i]!.trimStart().startsWith(fence)) code.push(lines[i++]!);
			i++;
			blocks.push({ type: 'code', text: code.join('\n') });
		} else if ((match = HEADING.exec(line))) {
			blocks.push({ type: 'heading', level: match[1]!.length, inline: parseInline(match[2]!) });
			i++;
		} else if (RULE.test(line)) {
			blocks.push({ type: 'rule' });
			i++;
		} else if (QUOTE.test(line)) {
			const quoted: string[] = [];
			while (i < lines.length && !blank(lines[i]) && (match = QUOTE.exec(lines[i]!) ?? (quoted.length ? [lines[i]!, lines[i]!] : null))) {
				quoted.push(match[1]!);
				i++;
			}
			blocks.push({ type: 'quote', blocks: parseBlocks(quoted.join('\n')) });
		} else if (BULLET.test(line) || NUMBERED.test(line)) {
			const ordered = !BULLET.test(line);
			const pattern = ordered ? NUMBERED : BULLET;
			const start = ordered ? Number(NUMBERED.exec(line)![2]) : 1;
			const items: string[][] = [];
			while (i < lines.length) {
				const current = lines[i]!;
				const item = pattern.exec(current);
				if (item && item[1]!.length < 2) {
					items.push([item[3]!]);
				} else if (!blank(current) && items.length && (/^\s{2,}/.test(current) || !starts(current, lines[i + 1]))) {
					// an indented line (a list inside, or more of the item), or more text of the item
					items[items.length - 1]!.push(current.replace(/^\s{2,4}/, ''));
				} else if (blank(current) && items.length && /^\s{2,}\S/.test(lines[i + 1] ?? '')) {
					items[items.length - 1]!.push('');
				} else {
					break;
				}
				i++;
			}
			blocks.push({ type: 'list', ordered, start, items: items.map(item => parseBlocks(item.join('\n'))) });
		} else if (line.includes('|') && TABLE_RULE.test(lines[i + 1] ?? '') && (lines[i + 1] ?? '').includes('-')) {
			const head = tableCells(line);
			const align = tableCells(lines[i + 1]!).map((cell): Align =>
				cell.startsWith(':') && cell.endsWith(':') ? 'center' : cell.endsWith(':') ? 'right' : cell.startsWith(':') ? 'left' : '');
			i += 2;
			const rows: Inline[][][] = [];
			while (i < lines.length && !blank(lines[i]) && lines[i]!.includes('|')) {
				const cells = tableCells(lines[i]!);
				rows.push(head.map((_, c) => parseInline(cells[c] ?? '')));
				i++;
			}
			blocks.push({ type: 'table', align, head: head.map(cell => parseInline(cell)), rows });
		} else {
			const text: string[] = [];
			while (i < lines.length && !blank(lines[i]) && (!text.length || !starts(lines[i]!, lines[i + 1]))) text.push(lines[i++]!);
			blocks.push(...paragraph(text.join('\n')));
		}
	}
	return blocks;
}

/* Pokemon names */

const ADJECTIVES: Record<string, string> = { Alola: 'Alolan', Galar: 'Galarian', Hisui: 'Hisuian', Paldea: 'Paldean' };

/**
 * The names a post might call each Pokemon by (Showdown's, and draft sheets' "Mega Charizard Y" and "Alolan Ninetales"),
 * from its species' names: name -> species
 */
export function pokemonNames(species: { id: string, name: string }[]) {
	const names = new Map<string, string>();
	for (const { id, name } of species) {
		if (!name || name.length < 3) continue;
		names.set(name, id);
		const mega = /^(.+?)-Mega(?:-([XYZ]))?$/.exec(name);
		if (mega) names.set(`Mega ${mega[1]}${mega[2] ? ` ${mega[2]}` : ''}`, id);
		const regional = /^(.+?)-(Alola|Galar|Hisui|Paldea)$/.exec(name);
		if (regional) names.set(`${ADJECTIVES[regional[2]!]} ${regional[1]}`, id);
	}
	return names;
}

export type TextPiece = { text: string } | { pokemon: string, text: string };

/** A capitalised hyphenated name, maybe a Mega's ("Basculegion-M", "Eternal-Floette", "Mega Eternal-Floette") */
const SPELLING = "(?:Mega )?\\p{Lu}[\\p{L}\\p{N}.’']*(?:-\\p{Lu}[\\p{L}\\p{N}.’']*)+(?: [XYZ])?";

/**
 * Finds Pokemon names in some text, as whole words with their capitals ("Garchomp's", not "garchomp" or
 * "Garchompite"), the longest name first ("Mega Charizard Y" before "Charizard"). With `resolve` (the Pokemon data's
 * own lookup), other spellings draft sheets use are found too, if it knows them: "Basculegion-M", "Eternal-Floette".
 */
export function pokemonMatcher(names: Map<string, string>, resolve?: (name: string) => string | null) {
	const escaped = [...names.keys()].sort((a, b) => b.length - a.length).map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
	if (!escaped.length) return (text: string): TextPiece[] => [{ text }];
	const alternatives = resolve ? [...escaped, SPELLING] : escaped;
	const pattern = new RegExp(`(?<![\\p{L}\\p{N}’'-])(?:${alternatives.join('|')})(?![\\p{L}\\p{N}-])`, 'gu');
	return (text: string): TextPiece[] => {
		const pieces: TextPiece[] = [];
		let last = 0;
		for (const match of text.matchAll(pattern)) {
			const id = names.get(match[0]) ?? resolve?.(match[0]) ?? null;
			if (!id) continue;
			if (match.index > last) pieces.push({ text: text.slice(last, match.index) });
			pieces.push({ pokemon: id, text: match[0] });
			last = match.index + match[0].length;
		}
		if (last < text.length) pieces.push({ text: text.slice(last) });
		return pieces;
	};
}
